import { ArrowsClockwise, CaretRight, Globe, Lightning, TextAa } from '@phosphor-icons/react';
import { Card } from '@/components/ui';

export function ValuePillars() {
  return (
    <section id="features" className="bg-canvas py-32">
      <div className="mx-auto max-w-container px-6">
        <header className="mb-16 max-w-2xl">
          <h2 className="mb-4 font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Everything you need. Nothing you don't.
          </h2>
          <p className="text-lg text-ink-muted">
            No AI to write your bio for you. No 47-step onboarding. Just the stuff you actually need to put your work online.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:auto-rows-[320px]">
          <Card className="group relative flex flex-col justify-between overflow-hidden p-8 md:col-span-2">
            <div className="relative z-10">
              <IconBadge>
                <TextAa size={20} />
              </IconBadge>
              <h3 className="mb-2 font-display text-xl font-semibold">
                Granular typographic control
              </h3>
              <p className="max-w-sm text-sm text-ink-muted">
                Mix and match system fonts, Google fonts, or upload your own tracking,
                leading, and scale.
              </p>
            </div>
            <div className="pointer-events-none absolute -bottom-4 -right-4 h-[200px] w-[300px] rounded-tl-xl border-l border-t border-line bg-canvas p-4 shadow-subtle transition-transform duration-500 group-hover:-translate-x-2 group-hover:-translate-y-2">
              <div className="flex flex-col gap-3">
                <Row label="Heading Font" value="Newsreader" />
                <Row label="Weight" value="Medium (500)" />
                <div className="mt-2 font-editorial text-4xl text-ink">Aa</div>
              </div>
            </div>
          </Card>

          <Card className="relative flex flex-col overflow-hidden p-8">
            <IconBadge>
              <Lightning size={20} />
            </IconBadge>
            <h3 className="mb-2 font-display text-xl font-semibold">Edge-deployed</h3>
            <p className="relative z-10 text-sm text-ink-muted">
              Loads fast. We won't tell you the exact milliseconds because we don't want to jinx it.
            </p>
            <div className="absolute -bottom-8 left-0 right-0 flex justify-center opacity-80">
              <div className="flex h-32 w-32 items-center justify-center rounded-full border-[8px] border-emerald-400">
                <span className="font-mono text-3xl font-bold text-emerald-500">97</span>
              </div>
            </div>
          </Card>

          <Card className="relative flex flex-col overflow-hidden p-8">
            <IconBadge>
              <Globe size={20} />
            </IconBadge>
            <h3 className="mb-2 font-display text-xl font-semibold">Bring your domain</h3>
            <p className="relative z-10 text-sm text-ink-muted">
            Use your own domain. SSL included. If DNS confuses you, we have a guide. If the guide confuses you, just use the free subdomain.
            </p>
            <div className="absolute inset-x-8 bottom-8 flex items-center rounded-md border border-line bg-canvas px-4 py-3 font-mono text-sm text-ink-faint shadow-subtle">
              https://
              <span className="ml-1 animate-pulse border-r border-ink pr-1 text-ink">
                yourname.com
              </span>
            </div>
          </Card>

          <Card className="group relative flex flex-row items-center justify-between overflow-hidden p-8 md:col-span-2">
            <div className="z-10 max-w-xs">
              <IconBadge>
                <ArrowsClockwise size={20} />
              </IconBadge>
              <h3 className="mb-2 font-display text-xl font-semibold">Syncs with your tools</h3>
              <p className="text-sm text-ink-muted">
                Type. Save. Publish. That's the whole workflow. We know Notion sync sounds cool — maybe one day.
              </p>
            </div>
            <div className="relative z-10 hidden items-center gap-4 pr-8 sm:flex">
              <div className="z-10 flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-board shadow-sm">
                <span className="font-editorial text-xl font-bold">N</span>
              </div>
              <div className="relative w-16 border-t border-dashed border-line">
                <CaretRight
                  weight="fill"
                  size={14}
                  className="absolute -right-2 -top-[7px] text-line"
                />
              </div>
              <div className="outline-board z-10 flex h-14 w-14 items-center justify-center rounded-xl bg-ink shadow-float outline outline-4">
                <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-board">
                  <div className="h-1.5 w-1.5 rounded-full bg-ink" />
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

function IconBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-6 inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-canvas text-ink">
      {children}
    </span>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-line pb-2 text-xs text-ink-muted">
      <span>{label}</span>
      <span className="rounded border border-line bg-board px-2 py-1">{value}</span>
    </div>
  );
}
